/**
 * Clue Builder Store
 * Manages state for the opener clue building phase
 */

import { writable, derived } from 'svelte/store';
import type {
  Category,
  Prompt,
  Clue,
  SlotSelection,
} from '@blather/shared';
import type {
  OpenerFrameworksData,
  FrameworkWithSlots,
  SlotWithWords,
} from '@blather/shared';

interface ClueBuilderState {
  category: Category | null;
  prompt: Prompt | null;
  frameworks: FrameworkWithSlots[];
  selectedFrameworkId: string | null;
  currentSlotIndex: number; // Current keyboard focus
  selectedWords: Map<number, string[]>; // slotIndex → words in selection order
  focusedWordIndices: Map<number, number>; // slotIndex → focused word index
  isSubmitted: boolean;
  loading: boolean;
  error: string | null;
  mode: 'opener' | 'clue'; // Opener clue or presentation clue
}

const initialState: ClueBuilderState = {
  category: null,
  prompt: null,
  frameworks: [],
  selectedFrameworkId: null,
  currentSlotIndex: 0,
  selectedWords: new Map(),
  focusedWordIndices: new Map(),
  isSubmitted: false,
  loading: false,
  error: null,
  mode: 'opener',
};

function createClueBuilderStore() {
  const { subscribe, set, update } = writable<ClueBuilderState>(initialState);

  return {
    subscribe,

    /**
     * Initialize with frameworks from server (for opener)
     * Auto-selects a random framework instead of letting player choose
     */
    setFrameworks(data: OpenerFrameworksData) {
      update((state) => {
        // Randomly select a framework
        const randomIndex = Math.floor(Math.random() * data.frameworks.length);
        const selectedFramework = data.frameworks[randomIndex];

        const newState = {
          ...state,
          mode: 'opener' as const,
          category: data.category,
          prompt: data.prompt,
          frameworks: data.frameworks,
          selectedFrameworkId: selectedFramework?.framework.id || null,
          currentSlotIndex: 0,
          selectedWords: new Map(),
          focusedWordIndices: new Map(),
          loading: false,
          error: null,
        };

        // Initialize focusedWordIndices to 0 for each slot
        if (selectedFramework) {
          selectedFramework.slots.forEach((_, index) => {
            newState.focusedWordIndices.set(index, 0);
          });
        }

        return newState;
      });
    },

    /**
     * Initialize with frameworks for presentation clue
     * Auto-selects a random framework instead of letting player choose
     */
    setFrameworksForClue(data: OpenerFrameworksData) {
      update((state) => {
        // Randomly select a framework
        const randomIndex = Math.floor(Math.random() * data.frameworks.length);
        const selectedFramework = data.frameworks[randomIndex];

        const newState = {
          ...state,
          mode: 'clue' as const,
          category: data.category,
          prompt: data.prompt,
          frameworks: data.frameworks,
          selectedFrameworkId: selectedFramework?.framework.id || null,
          currentSlotIndex: 0,
          selectedWords: new Map(),
          focusedWordIndices: new Map(),
          isSubmitted: false,
          loading: false,
          error: null,
        };

        // Initialize focusedWordIndices to 0 for each slot
        if (selectedFramework) {
          selectedFramework.slots.forEach((_, index) => {
            newState.focusedWordIndices.set(index, 0);
          });
        }

        return newState;
      });
    },

    /**
     * Switch to a different framework template
     */
    selectFramework(frameworkId: string) {
      update((state) => {
        const framework = state.frameworks.find((f) => f.framework.id === frameworkId);
        if (!framework) return state;

        const newState = {
          ...state,
          selectedFrameworkId: frameworkId,
          currentSlotIndex: 0,
          selectedWords: new Map(),
          focusedWordIndices: new Map(),
        };

        // Initialize focusedWordIndices
        framework.slots.forEach((_, index) => {
          newState.focusedWordIndices.set(index, 0);
        });

        return newState;
      });
    },

    /**
     * Move slot focus (Tab/Shift+Tab - wraps around)
     */
    moveSlot(direction: 'next' | 'prev') {
      update((state) => {
        const framework = state.frameworks.find((f) => f.framework.id === state.selectedFrameworkId);
        if (!framework) return state;

        const slotCount = framework.slots.length;
        let newIndex = state.currentSlotIndex;

        if (direction === 'next') {
          newIndex = (state.currentSlotIndex + 1) % slotCount;
        } else {
          newIndex = (state.currentSlotIndex - 1 + slotCount) % slotCount;
        }

        return { ...state, currentSlotIndex: newIndex };
      });
    },

    /**
     * Move word focus within current slot (Arrow Up/Down - NO wrap, stops at boundaries)
     */
    moveWordFocus(direction: 'up' | 'down') {
      update((state) => {
        const framework = state.frameworks.find((f) => f.framework.id === state.selectedFrameworkId);
        if (!framework) return state;

        const currentSlot = framework.slots[state.currentSlotIndex];
        if (!currentSlot) return state;

        const currentFocus = state.focusedWordIndices.get(state.currentSlotIndex) || 0;
        const maxIndex = currentSlot.availableWords.length - 1;

        let newFocus = currentFocus;

        if (direction === 'down') {
          newFocus = Math.min(currentFocus + 1, maxIndex);
        } else {
          newFocus = Math.max(currentFocus - 1, 0);
        }

        const newFocusedWordIndices = new Map(state.focusedWordIndices);
        newFocusedWordIndices.set(state.currentSlotIndex, newFocus);

        return {
          ...state,
          focusedWordIndices: newFocusedWordIndices,
        };
      });
    },

    /**
     * Add focused word to current slot (Enter key)
     * Enforces "not" first rule for adjective slots
     */
    addWord() {
      update((state) => {
        const framework = state.frameworks.find((f) => f.framework.id === state.selectedFrameworkId);
        if (!framework) return state;

        const currentSlot = framework.slots[state.currentSlotIndex];
        if (!currentSlot) return state;

        const focusedWordIndex = state.focusedWordIndices.get(state.currentSlotIndex) || 0;
        const wordToAdd = currentSlot.availableWords[focusedWordIndex];
        if (!wordToAdd) return state;

        const currentSelected = state.selectedWords.get(state.currentSlotIndex) || [];

        // Check if word already selected
        if (currentSelected.includes(wordToAdd)) {
          return state;
        }

        // Check max per slot
        if (currentSelected.length >= currentSlot.maxPerSlot) {
          return state;
        }

        // Special rule: "not" must be first in adjective slots
        if (currentSlot.slotType === 'adjective' && wordToAdd === 'not') {
          if (currentSelected.length > 0) {
            // Can't add "not" if words already selected
            return {
              ...state,
              error: '"not" must be first in adjective slots',
            };
          }
        }

        // Special rule: if slot is adjective and already has "not", enforce it's first
        if (currentSlot.slotType === 'adjective' && currentSelected.includes('not')) {
          // Already has "not", so we can add more words
        }

        const newSelectedWords = new Map(state.selectedWords);
        newSelectedWords.set(state.currentSlotIndex, [...currentSelected, wordToAdd]);

        return {
          ...state,
          selectedWords: newSelectedWords,
          error: null,
        };
      });
    },

    /**
     * Remove last word from current slot (Backspace)
     */
    removeLastWord() {
      update((state) => {
        const currentSelected = state.selectedWords.get(state.currentSlotIndex) || [];
        if (currentSelected.length === 0) return state;

        const newSelectedWords = new Map(state.selectedWords);
        newSelectedWords.set(state.currentSlotIndex, currentSelected.slice(0, -1));

        return {
          ...state,
          selectedWords: newSelectedWords,
        };
      });
    },

    /**
     * Clear entire current slot (Delete)
     */
    clearSlot() {
      update((state) => {
        const newSelectedWords = new Map(state.selectedWords);
        newSelectedWords.set(state.currentSlotIndex, []);

        return {
          ...state,
          selectedWords: newSelectedWords,
        };
      });
    },

    /**
     * Build clue object for submission
     */
    buildClue(state: ClueBuilderState): Clue | null {
      const framework = state.frameworks.find((f) => f.framework.id === state.selectedFrameworkId);
      if (!framework) return null;

      const slots: SlotSelection[] = framework.slots.map((slot, index) => {
        const selectedWords = state.selectedWords.get(index) || [];
        return {
          slotType: slot.slotType,
          selectedWords,
        };
      });

      return {
        frameworkId: framework.framework.id,
        template: framework.framework.template,
        slots,
      };
    },

    /**
     * Render clue preview as readable text
     */
    renderCluePreview(state: ClueBuilderState): string {
      const framework = state.frameworks.find((f) => f.framework.id === state.selectedFrameworkId);
      if (!framework) return '';

      let rendered = framework.framework.template;

      framework.slots.forEach((slot, index) => {
        const selectedWords = state.selectedWords.get(index) || [];
        const wordsText = selectedWords.length > 0 ? selectedWords.join(' ') : `[${slot.defaultWord}]`;
        const placeholder = `[${slot.slotType}]`;
        // Only replace first occurrence to handle duplicate slot types
        rendered = rendered.replace(placeholder, wordsText);
      });

      return rendered;
    },

    /**
     * Mark as submitted
     */
    setSubmitted(submitted: boolean) {
      update((state) => ({ ...state, isSubmitted: submitted }));
    },

    /**
     * Set loading state
     */
    setLoading(loading: boolean) {
      update((state) => ({ ...state, loading }));
    },

    /**
     * Set error
     */
    setError(error: string | null) {
      update((state) => ({ ...state, error }));
    },

    /**
     * Reset to initial state
     */
    reset() {
      set(initialState);
    },
  };
}

export const clueBuilderStore = createClueBuilderStore();

// Derived store for clue preview
export const cluePreview = derived(
  clueBuilderStore,
  ($store) => clueBuilderStore.renderCluePreview($store)
);

// Derived store for current framework
export const currentFramework = derived(
  clueBuilderStore,
  ($store) => $store.frameworks.find((f) => f.framework.id === $store.selectedFrameworkId) || null
);

// Derived store for current slot
export const currentSlot = derived(
  [clueBuilderStore, currentFramework],
  ([$store, $framework]) => {
    if (!$framework) return null;
    return $framework.slots[$store.currentSlotIndex] || null;
  }
);

// Derived store for checking if clue is complete
export const isClueComplete = derived(
  [clueBuilderStore, currentFramework],
  ([$store, $framework]) => {
    if (!$framework) return false;

    return $framework.slots.every((_, index) => {
      const selectedWords = $store.selectedWords.get(index) || [];
      return selectedWords.length > 0;
    });
  }
);
